<script setup lang="ts">
    import { MEILISEARCH_MODEL } from '~/app/common/enums/MeilisearchIndexType';
    import type { ContactsIndexSchema } from '~/app/common/schemas/ContactsIndexSchema';
    import { useSearchDropdown } from '~/composables/useSearchDropdown';

    interface Fields {
        contactType: string;
        firstName: string;
        lastName: string;
        companyName: string;
        email: string;
        phone: string;
    }

    const props = defineProps<{
        searchTerm: string;
    }>();

    const emit = defineEmits<{
        submit: [data: { contactType: string; firstName: string; lastName: string; companyName?: string; email?: string; phone?: string }];
        cancel: [];
        'select-existing': [uuid: string];
    }>();

    const {
        results: contactResults,
        showDropdown,
        search: searchMeilisearchContacts,
        hideDropdown,
    } = useSearchDropdown<ContactsIndexSchema>({ model: MEILISEARCH_MODEL.CONTACTS, debounceMs: 200 });

    const parts = props.searchTerm.trim().split(/\s+/);
    const form = useForm<Fields>({
        contactType: 'client',
        firstName: parts[0] ?? '',
        lastName: parts.slice(1).join(' ') ?? '',
        companyName: '',
        email: '',
        phone: '',
    });

    const contactTypeOptions = [
        { value: 'client', text: 'Client' },
        { value: 'supplier', text: 'Fournisseur' },
        { value: 'subcontractor', text: 'Sous-traitant' },
        { value: 'other', text: 'Autre' },
    ];

    const triggerSearch = () => {
        const query = `${form.fields.firstName} ${form.fields.lastName}`.trim();
        searchMeilisearchContacts(query);
    };

    const selectExisting = (contact: ContactsIndexSchema) => {
        showDropdown.value = false;
        emit('select-existing', contact.uuid);
    };

    const submitForm = () => {
        emit('submit', {
            contactType: form.fields.contactType,
            firstName: form.fields.firstName,
            lastName: form.fields.lastName,
            companyName: form.fields.companyName || undefined,
            email: form.fields.email || undefined,
            phone: form.fields.phone || undefined,
        });
    };
</script>

<template>
    <div class="rounded-[12px] border border-[#E7E7E7] bg-white overflow-hidden shadow-[0_1px_2px_rgba(15,16,32,0.04)]">
        <!-- Header band -->
        <div
            class="flex items-center gap-[10px] px-[14px] py-3 border-b border-[#E7E7E7]"
            style="background: linear-gradient(180deg, #FEF8EA 0%, #FEFCF6 100%)"
        >
            <span class="w-6 h-6 rounded-full bg-branding-brand-yellow border border-[#E8B102] flex items-center justify-center flex-shrink-0 shadow-sm">
                <i class="fa-solid fa-user text-[11px] text-black" />
            </span>
            <div class="flex-1 min-w-0">
                <p class="font-bricolage font-semibold text-[14px] text-gray-900 leading-[18px]">
                    {{ $t('ai.form.new_contact') }}
                </p>
                <p class="font-inter text-[11px] text-gray-400 mt-[1px] truncate">
                    {{ $t('ai.form.from', { seed: searchTerm || '...' }) }}
                </p>
            </div>
        </div>

        <!-- Body -->
        <div class="px-[14px] py-[14px] flex flex-col gap-[12px]">
            <!-- Name fields with Meilisearch dropdown -->
            <div class="grid grid-cols-2 gap-[10px] relative">
                <FormInput
                    :form="form"
                    input-name="firstName"
                    @input="triggerSearch"
                    @blur="hideDropdown"
                />
                <FormInput
                    :form="form"
                    input-name="lastName"
                    @input="triggerSearch"
                    @blur="hideDropdown"
                />
                <div
                    v-if="showDropdown && contactResults.length"
                    class="absolute top-full left-0 right-0 mt-[4px] bg-white border border-[#E7E7E7] rounded-md shadow-md z-10 max-h-[180px] overflow-y-auto"
                >
                    <button
                        v-for="contact in contactResults"
                        :key="contact.uuid"
                        type="button"
                        class="w-full text-left px-3 py-2 hover:bg-[#FEFCF6] border-b border-gray-50 last:border-b-0"
                        @mousedown.prevent="selectExisting(contact)"
                    >
                        <span class="block font-inter text-[12px] text-gray-900 truncate">{{ contact.display_name }}</span>
                        <span v-if="contact.email" class="block font-inter text-[10px] text-gray-400">{{ contact.email }}</span>
                    </button>
                </div>
            </div>

            <FormSelect
                :form="form"
                input-name="contactType"
                :options="contactTypeOptions"
            />

            <FormInput
                :form="form"
                input-name="companyName"
            />

            <div class="grid grid-cols-2 gap-[10px]">
                <FormEmailInput
                    :form="form"
                    input-name="email"
                />
                <FormPhoneInput
                    :form="form"
                    input-name="phone"
                />
            </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-2 px-[14px] py-[10px] border-t border-[#E7E7E7] bg-branding-sea-salt">
            <button
                type="button"
                class="px-3 py-[7px] font-inter text-[13px] font-medium text-gray-700 bg-white border border-[#D3D2D2] rounded-md hover:bg-gray-50 shadow-sm cursor-pointer"
                @click="emit('cancel')"
            >
                {{ $t('actions.cancel') }}
            </button>
            <button
                type="button"
                class="px-3 py-[7px] font-inter text-[13px] font-medium rounded-md shadow-sm transition-colors"
                :class="!form.fields.firstName && !form.fields.lastName
                    ? 'opacity-40 cursor-not-allowed bg-branding-brand-yellow border border-[#E8B102] text-black'
                    : 'bg-branding-brand-yellow border border-[#E8B102] text-black hover:bg-branding-mustard cursor-pointer'"
                :disabled="!form.fields.firstName && !form.fields.lastName"
                @click="submitForm"
            >
                {{ $t('actions.create_contact') }}
            </button>
        </div>
    </div>
</template>
